// src/pages/UploadProductPage.jsx
import React, { useEffect, useRef, useState } from 'react';
import { ProgressBar } from 'react-bootstrap';
import { countries } from 'countries-list';
import { createProduct } from '../services/productService';
import { uploadProductFiles, clearPendingUpload } from '../services/chunkedUpload';
import '../styles/UploadProductPage.css';
import '../styles/ProgressBar.css';

const CATEGORY_OPTIONS = [
  'BIM Data',
  'Herramientas Pro',
  'Recursos Visuales',
  'Guías / Documentos',
  'Plugins / Scripts',
  'Otros'
];
const SPECIALTY_OPTIONS = [
  'Arquitectura',
  'Estructuras',
  'Geotecnia',
  'Pavimentos',
  'Hidrosanitaria',
  'Hidráulica',
  'Vialidad / Transporte',
  'Construcción y Obra',
  'BIM & Coordinación',
  'Instalaciones MEP',
  'Ingeniería Eléctrica',
  'Telecomunicaciones / ICT',
  'Ambiental / Sostenibilidad',
  'Topografía & GIS',
  'Otros'
];

const MAX_DESCRIPTION_LENGTH = 2000;
const MAX_FILE_SIZE_MB = 1000; // Límite de tamaño por archivo en MB (1 GB)
const MAX_PHOTO_SIZE_MB = 5;  // Límite de tamaño para la foto en MB

const FIELD_LABELS = {
  nombre: 'Nombre del recurso',
  descripcionProd: 'Descripción',
  precioIndividual: 'Precio',
  categorias: 'Categorías',
  especialidades: 'Especialidades',
  pais: 'País',
  fotografiaProd: 'Imágenes de portada',
  archivosAut: 'Archivos del producto',
};

const blank = {
  nombre: '',
  descripcionProd: '',
  precioIndividual: '',
  pais: '',
  fotografiaProd: [],
  archivosAut: [],
};

export default function UploadProductPage() {
  const [form, setForm] = useState(blank);
  const [selectedCategories, setCats] = useState([]);
  const [selectedSpecialties, setSpecs] = useState([]);
  const [previewSrc, setPreview] = useState([]);
  const [errors, setErrors] = useState({});
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [feedback, setFeedback] = useState(null); // { ok } y se repite con cada envío

  const photoInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const feedbackRef = useRef(null);

  // Solo las claves con mensaje cuentan como error (los campos corregidos quedan en undefined).
  const errorKeys = Object.keys(errors).filter(k => errors[k]);
  const hasErrors = errorKeys.length > 0;

  useEffect(() => {
    // Cada visita al formulario abre un grupo/carpeta nuevo en Drive.
    clearPendingUpload();
  }, []);

  useEffect(() => {
    if (!feedback) return;
    const node = feedbackRef.current;
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
      node.focus?.();
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [feedback]);

  const toggle = (arr, v) => arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];

  const toggleCategory = cat => {
    setMsg(null);
    setCats(c => toggle(c, cat));
  };

  const toggleSpecialty = sp => {
    setMsg(null);
    setSpecs(s => toggle(s, sp));
  };

  const text = e => {
    const { name, value } = e.target;
    const newValue = name === 'descripcionProd' ? value.slice(0, MAX_DESCRIPTION_LENGTH) : value;
    setMsg(null);
    setForm(f => ({ ...f, [name]: newValue }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const file = field => e => {
    const incoming = Array.from(e.target.files || []);
    if (incoming.length === 0) return;

    setMsg(null);

    if (field === 'archivosAut') {
      // 1. Check for images in the file section
      const containsImages = incoming.some(f => f.type.startsWith('image/'));
      if (containsImages) {
        alert("No puedes cargar imágenes, llévalas a un PDF");
        e.target.value = "";
        return;
      }

      // 2. Individual file size check (1000MB / 1GB)
      const individualTooLarge = incoming.some(f => f.size > MAX_FILE_SIZE_MB * 1024 * 1024);
      if (individualTooLarge) {
        alert(`Lo sentimos, uno de tus archivos supera el límite individual de ${MAX_FILE_SIZE_MB} MB.`);
        e.target.value = "";
        return;
      }

      // 3. Cumulative size check (1000MB / 1GB total)
      const currentSize = form.archivosAut.reduce((acc, f) => acc + f.size, 0);
      const incomingSize = incoming.reduce((acc, f) => acc + f.size, 0);
      if ((currentSize + incomingSize) > MAX_FILE_SIZE_MB * 1024 * 1024) {
        alert("Lo sentimos, tus archivos superan el limite");
        e.target.value = "";
        return;
      }

      setForm(p => ({ ...p, archivosAut: [...p.archivosAut, ...incoming] }));
      setErrors(p => ({ ...p, archivosAut: undefined }));

    } else if (field === 'fotografiaProd') {
      // 1. Individual image size check (5MB)
      const individualTooLarge = incoming.some(f => f.size > MAX_PHOTO_SIZE_MB * 1024 * 1024);
      if (individualTooLarge) {
        alert(`Lo sentimos, una de tus imágenes supera el límite individual de ${MAX_PHOTO_SIZE_MB} MB.`);
        e.target.value = "";
        return;
      }

      // 2. Cumulative image size check (5MB total)
      const currentSize = form.fotografiaProd.reduce((acc, f) => acc + f.size, 0);
      const incomingSize = incoming.reduce((acc, f) => acc + f.size, 0);
      if ((currentSize + incomingSize) > MAX_PHOTO_SIZE_MB * 1024 * 1024) {
        alert("Lo sentimos, tus imágenes superan el limite.");
        e.target.value = "";
        return;
      }

      // Success: Update state and previews
      setForm(p => ({ ...p, fotografiaProd: [...p.fotografiaProd, ...incoming] }));
      setErrors(p => ({ ...p, fotografiaProd: undefined }));

      incoming.forEach(v => {
        const reader = new FileReader();
        reader.onload = ev => setPreview(prev => [...prev, ev.target.result]);
        reader.readAsDataURL(v);
      });
    }
  };



  const validate = () => {
    const e = {};
    if (!form.nombre.trim()) e.nombre = 'Ingresa el nombre de tu recurso';
    if (!form.descripcionProd.trim()) e.descripcionProd = 'Ingresa una descripción';
    // Permitir FREE con 0.00; bloquear negativos y > 99
    if (
      form.precioIndividual === '' ||
      isNaN(parseFloat(form.precioIndividual)) ||
      parseFloat(form.precioIndividual) < 0 ||
      parseFloat(form.precioIndividual) > 99
    ) {
      e.precioIndividual = 'El precio debe ser 0.00 (Free) o estar entre $1.00 y $99.00';
    }
    if (selectedCategories.length === 0) e.categorias = 'Selecciona al menos 1 categoría';
    if (selectedSpecialties.length === 0) e.especialidades = 'Selecciona al menos 1 especialidad';
    if (!form.pais) e.pais = 'Selecciona un país';
    if (form.fotografiaProd.length === 0) e.fotografiaProd = 'Sube al menos 1 imagen';
    if (form.archivosAut.length === 0) e.archivosAut = 'Sube al menos 1 archivo';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const descripcionPreservada = form.descripcionProd.replace(/\r\n/g, '\n');

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validate()) {
      setFeedback({ ok: false });
      return;
    }

    const precio = parseFloat(form.precioIndividual);
    const esGratis = !isNaN(precio) && precio === 0;

    const payload = {
      nombre: form.nombre,
      descripcionProd: descripcionPreservada,
      precioIndividual: precio,
      ...(esGratis ? { esGratis: true } : {}),      pais: form.pais,
      categorias: selectedCategories,
      especialidades: selectedSpecialties,
    };

    try {
      setLoading(true);
      setProgress(0);

      // Subida reanudable: los archivos van directos a file-service por chunks.
      const files = [...form.fotografiaProd, ...form.archivosAut];
      const { groupKey } = await uploadProductFiles(files, { onProgress: setProgress });

      const fd = new FormData();
      fd.append('dto', new Blob([JSON.stringify({ ...payload, groupKey })], { type: 'application/json' }));

      await createProduct(fd);
      clearPendingUpload();

      setMsg({ ok: true, txt: '¡Tu recurso fue enviado! Nuestro equipo lo revisará y te notificará pronto por correo electrónico.' });
      setForm(blank);
      setCats([]);
      setSpecs([]);
      setPreview([]);
      setErrors({});
      if (photoInputRef.current) photoInputRef.current.value = '';
      if (fileInputRef.current) fileInputRef.current.value = '';
      setFeedback({ ok: true });
    } catch (error) {
      console.error("Error al subir recurso:", error);
      setMsg({ ok: false, txt: 'Error al subir el recurso. Por favor, inténtalo de nuevo.' });
      setFeedback({ ok: false });
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  const Error = k => errors[k] && <div className="text-danger small mt-1">{errors[k]}</div>;

  return (
    <div className="container my-5">
      <div className="card shadow-sm border-0">
        <div className="card-body p-5">
          <h2 className="text-center fw-bold mb-2">
            PUBLICA TU RECURSO
          </h2>
          <p className="text-center mb-4 text-muted">
            Completa este formulario y pon tu recurso AEC a disposición de la comunidad en minutos.
          </p>
          {(msg || hasErrors) && (
            <div
              ref={feedbackRef}
              tabIndex={-1}
              role="alert"
              className={`upload-feedback alert ${msg && msg.ok && !hasErrors ? 'alert-success' : 'alert-danger'}`}
            >
              {msg && <div>{msg.txt}</div>}
              {hasErrors && (
                <>
                  {msg && <hr className="my-2" />}
                  <strong>Revisa los siguientes campos:</strong>
                  <ul className="mb-0 mt-2">
                    {errorKeys.map(k => (
                      <li key={k}>{FIELD_LABELS[k] || k}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="row g-4">
              {/* Nombre y precio */}
              <div className="col-md-6">
                <label className="form-label">Nombre del Recurso</label>
                <input
                  name="nombre"
                  className="form-control"
                  value={form.nombre}
                  onChange={text}
                  placeholder="Ej: Plantilla de Revit para diseño estructural"
                />
                {Error('nombre')}
              </div>
              <div className="col-md-6">
                <label className="form-label">Precio ($)</label>
                <input
                  name="precioIndividual"
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-control"
                  value={form.precioIndividual}
                  onChange={text}
                  placeholder="Ingresa 0.00 para Free o un precio entre 1.00 y 99.00, puedes actualizarlo luego"
                 />   
                 <small className="form-text text-muted">
                  Tip: 0.00 marca tu recurso como <strong>Free</strong>.
                </small>            
                {Error('precioIndividual')}
              </div>
            </div>

            <hr className="my-4" /> {/* Divisor visual */}

            {/* Categorías */}
            <div className="col-12 category-checkbox">
              <label className="form-label">Categorías</label>
              <div className="d-flex flex-wrap gap-3">
                {CATEGORY_OPTIONS.map(cat => (
                  <div className="form-check" key={cat}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`cat-${cat}`}
                      checked={selectedCategories.includes(cat)}
                      onChange={() => toggleCategory(cat)}
                      disabled={loading}
                    />
                    <label htmlFor={`cat-${cat}`} className="form-check-label">{cat}</label>
                  </div>
                ))}
              </div>
              {Error('categorias')}
            </div>

            {/* Especialidades */}
            <div className="col-12 specialty-checkbox">
              <label className="form-label">Especialidades</label>
              <div className="d-flex flex-wrap gap-3">
                {SPECIALTY_OPTIONS.map(sp => (
                  <div className="form-check" key={sp}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`sp-${sp}`}
                      checked={selectedSpecialties.includes(sp)}
                      onChange={() => toggleSpecialty(sp)}
                      disabled={loading}
                    />
                    <label htmlFor={`sp-${sp}`} className="form-check-label">{sp}</label>
                  </div>
                ))}
              </div>
              {Error('especialidades')}
            </div>

            <hr className="my-4" /> {/* Divisor visual */}

            {/* Descripción */}
            <div className="col-12">
              <label className="form-label">Descripción</label>
              <textarea
                name="descripcionProd"
                rows="6"
                className="form-control"
                value={form.descripcionProd}
                onChange={text}
                maxLength={MAX_DESCRIPTION_LENGTH}
                placeholder="Explica qué resuelve tu recurso, para quién está pensado y cómo se usa. Máximo 2000 caracteres."
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
              />
              <div className="text-end text-muted small mt-1">
                {form.descripcionProd.length}/{MAX_DESCRIPTION_LENGTH}
              </div>
              {Error('descripcionProd')}
            </div>

            <hr className="my-4" /> {/* Divisor visual */}

            <div className="row g-4">
              {/* País */}
              <div className="col-md-6">
                <label className="form-label">País</label>
                <select name="pais" className="form-select" value={form.pais} onChange={text}>
                  <option value="">Selecciona tu país</option>
                  {Object.keys(countries).map(c => (
                    <option key={c} value={countries[c].name}>{countries[c].name}</option>
                  ))}
                </select>
                {Error('pais')}
              </div>

              {/* Imágenes */}
              <div className="col-md-6">
                <label className="form-label">Imágenes de Portada. </label>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="form-control"
                  onChange={file('fotografiaProd')}
                  disabled={loading}
                />
                <small className="form-text text-muted">
                  Tamaño máximo: {MAX_PHOTO_SIZE_MB} MB.
                </small>
                {Error('fotografiaProd')}

                {previewSrc.length > 0 && (
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {previewSrc.map((src, i) => (
                      <img
                        key={i}
                        src={src}
                        alt={`Vista previa ${i + 1}`}
                        className="rounded"
                        style={{ maxHeight: 120 }}
                      />
                    ))}
                  </div>
                )}

                {/* Lista de imágenes seleccionadas con botón para quitar */}
                {form.fotografiaProd.length > 0 && (
                  <ul className="list-group mt-2">
                    {form.fotografiaProd.map((f, idx) => (
                      <li
                        key={idx}
                        className="list-group-item d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <i className="bi bi-image me-2" />
                          {f.name}
                        </div>
                        <span className="badge bg-secondary">
                          {(f.size / 1024 / 1024).toFixed(2)} MB
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => {
                            // quitar imagen y su preview en el mismo índice
                            setForm(prev => ({
                              ...prev,
                              fotografiaProd: prev.fotografiaProd.filter((_, j) => j !== idx)
                            }));
                            setPreview(prev => prev.filter((_, j) => j !== idx));
                          }}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>


              {/* Archivos */}
              <div className="col-12">
                <label className="form-label">Archivo(s) del Producto *</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="form-control"
                  onChange={file('archivosAut')}
                  disabled={loading}
                />
                <small className="form-text text-muted">
                  Tamaño máximo: {MAX_FILE_SIZE_MB} MB.
                </small>
                <small className="form-text text-muted">
                  Si tu recurso supera el peso, te recomendamos subirlo a tu nube, copia y pega el enlace en un TXT, y carga dicho archivo.
                </small>
                {Error('archivosAut')}
                <ul className="list-group mt-2">
                  {form.archivosAut.map((f, idx) => (
                    <li
                      key={idx}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <i className="bi bi-file-earmark-fill me-2" />
                        {f.name}
                      </div>
                      <span className="badge bg-secondary">
                        {(f.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() =>
                          setForm(prev => ({
                            ...prev,
                            archivosAut: prev.archivosAut.filter((_, j) => j !== idx)
                          }))
                        }
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <hr className="my-4" /> {/* Divisor visual */}

            <div className="text-center mt-4">
              <button type="submit" className="btn btn-primary px-5 py-2" disabled={loading}>
                {loading ? 'Publicando…' : 'Publicar Recurso'}
              </button>

              {loading && (
                <div className="aec-progress mt-3">
                  <ProgressBar
                    now={progress || 100}
                    striped
                    animated
                    label={progress ? `${progress}%` : 'Preparando…'}
                  />
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
